import { useEffect, useRef, useState } from "react";
import { CursorProps, NodeApi, Tree, TreeApi } from "react-arborist";
import { useSelector } from "react-redux";
import { ResursTreeApi } from "../../services/modules/tree";
import { clearSelectNodes, selectNodes } from "../../services/slices/tree";
import { RootState, useAppDispatch } from "../../services/types/store-types";
import { convertArrayToTree } from "../stub";
import { Node } from "./node/node";
import { ResursTreePanel } from "./resurs-tree-panel/resurs-tree-panel";
import styles from "./tree.module.css";

import { ResursCreateModal } from "./dialogs/create-modal";

import { DragPreview } from "./drag-preview/drag-preview";
import { handleRunTask } from "./utils/handleRunTask";

import { SelectionFilterModal } from "./dialogs/selection-filter-modal";
import { handleDelete } from "./utils/handleDelete";
import { handleResize } from "./utils/handleResizeTree";

import { debounce, isEqual, memoize } from "lodash";
import { ConfirmDialogDelete } from "./dialogs/confirm-dialog-delete";

import clsx from "clsx";
import { useLocation } from "react-router-dom";
import { useAuthContext } from "../../context/AuthContext";
import { useRpc } from "../../context/RpcContext";
import { useToast } from "../../context/toast/ui";
import { useHotkeys } from "../../lib/useHotkeys";
import useScrollRestoration from "../../utils/useScrollRestoration";
import useContextMenu from "./hooks/useContextMenu";
import { TResursTreeNode } from "./treeTypes";
import { QuickCreateModalUser } from "./dialogs/quick-create-user/quick-create-user";
import { QuickCreateModalDevice } from "./dialogs/quick-create-device/quick-create-device";

const convertArrayToTreeMemoized = memoize(convertArrayToTree);

interface IResursTree {
  title: string;
  treeName: ResursTreeApi.treeName;
  halfHeight?: boolean;
  treeNeighboor?: ResursTreeApi.treeName;
  disabledDnd?: boolean;
}
const ResursTree = (props: IResursTree) => {
  const {
    title,
    treeName,
    halfHeight,
    treeNeighboor,
    disabledDnd = false,
  } = props;
  const { userRole, isAuthenticated } = useAuthContext();
  const isAdmin = userRole !== "usr";
  const [isActiveButton, setIsActiveButton] = useState<boolean>(false);

  const treeInstance = useRef<TreeApi<any>>(null);
  const dispatch = useAppDispatch();
  const treeData = useSelector((state: RootState) => state.tree[treeName]);
  const location = useLocation();
  const lastSelect = useSelector(
    (state: RootState) => state.tree.selectedNodes
  );

  const { showMessage } = useToast();

  const [panelHeight, setPanelHeight] = useState(0);
  const [treeObjectListAvailableToAdd, setTreeObjectListAvailableToAdd] =
    useState<ResursTreeApi.TGetTreeObjectListAvailableToAddResponse | null>(
      null
    );

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showQuick, setShowQuick] = useState(false);
  const [term, setTerm] = useState("");
  const [visibleConfirmDelete, setVisibleConfirmDelete] = useState(false);
  const [selectionFilterData, setSelectionFilterData] = useState(null);
  const [selectionFilterModal, setSelectionFilterModal] = useState(false);
  const [findedItemInPanelId, setFindedItemInPanelId] = useState<number | null>(
    null
  );

  const handleArrowDown = () => {
    const selectedNodes = treeInstance.current?.selectedNodes;
    if (selectedNodes && selectedNodes.length > 0) {
      const currentNode = selectedNodes.at(-1);
      const nextNode = currentNode?.next;
      if (nextNode) {
        treeInstance.current.select(nextNode);
        handleSelect([nextNode]);
      }
    }
  };

  const handleArrowUp = () => {
    const selectedNodes = treeInstance.current?.selectedNodes;
    if (selectedNodes && selectedNodes.length > 0) {
      const currentNode = selectedNodes.at(-1);
      const prevNode = currentNode?.prev;
      if (prevNode) {
        treeInstance.current.select(prevNode);
        handleSelect([prevNode]);
      }
    }
  };

  // Обработчики горячих клавиш
  const handleDeleteKey = () => {
    if (
      isAdmin &&
      treeInstance.current &&
      treeInstance.current?.selectedNodes.length > 0 &&
      !location.pathname.includes("config")
    ) {
      setVisibleConfirmDelete(true);
    }
  };

  // Привязка горячих клавиш с помощью react-hotkeys-hook
  useHotkeys("delete", handleDeleteKey, {}, [
    handleDeleteKey,
    lastSelect,
    treeName,
  ]);
  useHotkeys("down", handleArrowDown, {}, [
    handleArrowDown,
    lastSelect,
    treeName,
  ]);
  useHotkeys("up", handleArrowUp, {}, [handleArrowUp, lastSelect, treeName]);

  // Подписка на события RPC

  interface ITreeSelect {
    tree: ResursTreeApi.treeName; // В каком дереве выделять элементы
    id: Array<number>; // id узлов для выделения
  }

  const { subscribe, wsOpened } = useRpc();

  useEffect(() => {
    if (findedItemInPanelId) {
      setTimeout(() => {
        const targetNode = treeInstance.current?.get(
          String(findedItemInPanelId)
        );
        if (!targetNode) {
          showMessage({
            caption: `Узел ${findedItemInPanelId} не найден`,
            type: "error",
            message: `Узел ${findedItemInPanelId} не найден`,
            tag: "tree",
            timeOut: 3,
          });
          return;
        }

        treeInstance.current?.selectMulti(String(findedItemInPanelId));

        setFindedItemInPanelId(null);
      }, 100);
    }
  }, [findedItemInPanelId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleTreeSelect = (params: ITreeSelect) => {
    treeInstance.current?.deselectAll();
    if (treeName === params.tree) {
      const combinedSelection = [...params.id];
      if (combinedSelection.length === 1) {
        combinedSelection.forEach((id) => {
          treeInstance.current?.openAll();
          setFindedItemInPanelId(id);
        }, 50);
      } else if (combinedSelection.length > 1) {
        treeInstance.current?.openAll();
        const firstId = String(combinedSelection[0]);
        treeInstance.current?.select(firstId);
        for (let i = 1; i < combinedSelection.length; i++) {
          treeInstance.current?.selectMulti(String(combinedSelection[i]));
        }
      }
    }
  };

  useEffect(() => {
    if (!wsOpened) return;

    subscribe(`tree:select@${treeName}`, handleTreeSelect);
  }, [treeName, wsOpened]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(ResursTreeApi.getTree(treeName)({ objectId: ["root"] }));
    }

    const _handleResize = debounce(() => {
      setPanelHeight(handleResize(halfHeight));
    }, 100);

    _handleResize(); // Установка начальной высоты

    window.addEventListener("resize", _handleResize);

    return () => {
      window.removeEventListener("resize", _handleResize);
    };
  }, [dispatch, treeName, halfHeight, wsOpened, isAuthenticated]);

  const selectedNodes = useSelector(
    (state: RootState) => state.tree.selectedNodes
  );

  /**
   * Тут возникает коллизия
   */
  useEffect(() => {
    if (!treeInstance.current) return;
    const objects = Array.from(treeInstance.current.selectedIds);
    if (treeName !== selectedNodes.tree) treeInstance.current.deselectAll();
    else {
      dispatch(
        ResursTreeApi.getPropertysRedux({
          objects: objects,
          tree: treeName,
        })
      );
    }
  }, [selectedNodes, treeName]);

  const handleClickOutside = (event: any) => {
    if (event.target.className === "tree_tree__vVRGQ treeInstanse") {
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const openMap = useSelector((state: RootState) => state.tree.openState);
  useEffect(() => {
    if (treeInstance.current) {
      Object.entries(openMap).forEach(([id, isOpen]) => {
        const node = treeInstance.current?.get(id);
        if (node && isOpen && !node.isOpen) {
          node.open();
        }
      });
    }
  }, [openMap]);
  const { cmRef } = useContextMenu();
  const [searchIndex, setSearchIndex] = useState(1);

  // Обработчик выбора узла
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSelect = (selected: NodeApi<TResursTreeNode>[]) => {
    if (!selected || selected.length === 0) return;
    const objectIds = selected.map((node) => node.id);

    dispatch(
      selectNodes({
        tree: treeName,
        objectIds,
        nodes: selected.map((node) => node.data),
      })
    );
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const handleScroll = useScrollRestoration(scrollRef, treeName);

  const prevSelectedNodesRef = useRef<NodeApi<TResursTreeNode>[]>([]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (treeInstance.current) {
      const currentSelectedNodes = Array.from(
        treeInstance.current.selectedNodes
      );
      const currentIds = currentSelectedNodes.map((node) => node.id);
      const prevIds = prevSelectedNodesRef.current.map((node) => node.id);
      if (!isEqual(currentIds, prevIds)) {
        handleSelect(currentSelectedNodes);
        prevSelectedNodesRef.current = currentSelectedNodes;
      }
    }
  };

  useEffect(() => {
    (async () => {
      if (treeName === "device") {
        const isAvailable = await ResursTreeApi.createDevcieTreeCheck({
          objectId: lastSelect.objectIds,
          what: "createDeviceTree",
        });
        setIsActiveButton(!isAvailable);
      }
    })();
  }, [lastSelect]);

  return (
    <div
      className={styles.ResursTree}
      ref={scrollRef}
      onScroll={handleScroll}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.header}>{title}</div>
      <ResursTreePanel
        createTreeButtonDisabled={isActiveButton}
        lastSelect={lastSelect}
        setSearchValue={setTerm}
        performSearch={(afterCallback) => {
          if (term.length > 0) {
            ResursTreeApi.searchInTree({
              pos: searchIndex,
              search: term,
              tree: treeName,
            }).then((res) => {
              if (res && treeInstance.current) {
                setSearchIndex(searchIndex + 1);
                const ids = res.objectId;
                treeInstance.current.deselectAll();
                ids.forEach((id) => treeInstance.current?.select("" + id));
                afterCallback();
              } else {
                setSearchIndex(1);
              }
            });
          }
        }}
        searchValue={term}
        tree={treeName}
        setSelectionFilterData={setSelectionFilterData}
        setSelectionFilterModal={setSelectionFilterModal}
        handleRunTask={handleRunTask}
        setShowQuick={setShowQuick}
        setShowCreateModal={setShowCreateModal}
        setTreeObjectListAvailableToAdd={setTreeObjectListAvailableToAdd}
      />
      <Tree
        disableDrag={disabledDnd}
        disableDrop={disabledDnd}
        width={"100%"}
        data={
          treeData.length > 0
            ? convertArrayToTreeMemoized(treeData, treeName)
            : []
        }
        className={clsx(styles.tree, "treeInstanse")}
        rowClassName={styles.row}
        initialOpenState={openMap}
        openByDefault={true}
        padding={0}
        ref={treeInstance}
        height={panelHeight}
        indent={32}
        rowHeight={32}
        renderDragPreview={DragPreview}
        renderCursor={Cursor}
        onContextMenu={(e) => {
          cmRef.current?.show(e);
        }}
      >
        {({ node, style, tree, dragHandle, preview }) => (
          <Node
            node={node}
            style={style}
            tree={tree}
            dragHandle={dragHandle}
            preview={preview}
            setVisibleConfirmDelete={setVisibleConfirmDelete}
          />
        )}
      </Tree>

      {visibleConfirmDelete && lastSelect.nodes.length && (
        <ConfirmDialogDelete
          accept={() => {
            if (userRole === "usr") {
              showMessage({
                caption: "Ошибка",
                message: "У вас нет прав!",
                tag: "add",
                timeOut: 3,
                type: "error",
              });

              return;
            }

            setVisibleConfirmDelete(false);
            handleDelete(treeName, lastSelect.nodes);
            dispatch(clearSelectNodes());
            treeInstance.current?.deselectAll();
          }}
          reject={() => {
            setVisibleConfirmDelete(false);
            dispatch(clearSelectNodes());
            treeInstance.current?.deselectAll();
          }}
          visibility={visibleConfirmDelete}
          setVisibility={() => setVisibleConfirmDelete(false)}
          message={
            lastSelect.nodes.length === 1
              ? `Вы действительно хотите удалить "${lastSelect.nodes[0].name}"${
                  lastSelect.tree &&
                  !["operator", "planner"].includes(lastSelect.tree)
                    ? " и все нижележащие элементы, если они есть"
                    : ""
                }?`
              : `Вы действительно хотите удалить объекты в количестве "${lastSelect.nodes.length}" штук?`
          }
        />
      )}
      {showQuick && (
        <>
          {treeName === "user" ? (
            <QuickCreateModalUser
              show={showQuick}
              setShow={setShowQuick}
              tree={treeName}
            />
          ) : (
            <QuickCreateModalDevice
              show={showQuick}
              setShow={setShowQuick}
              tree={treeName}
            />
          )}
        </>
      )}
      {treeObjectListAvailableToAdd && showCreateModal && (
        <ResursCreateModal
          show={showCreateModal}
          setShow={setShowCreateModal}
          createModalParams={treeObjectListAvailableToAdd}
          tree={treeName}
          ref={treeInstance}
        />
      )}
      {selectionFilterData && selectionFilterModal && (
        <SelectionFilterModal
          selectionFilterData={selectionFilterData}
          show={selectionFilterModal}
          setShow={setSelectionFilterModal}
          tree={treeName}
          treeNeighboor={treeNeighboor}
        />
      )}
    </div>
  );
};

function Cursor({ top, left }: CursorProps) {
  return (
    <div className={styles.dropCursor} style={{ top, left: left - 200 }}></div>
  );
}

export default ResursTree;
